import { useState } from "react";
import { MarketingLayout, Hero } from "../components/MarketingLayout";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const ROLES = ["GP practice", "Psychologist", "Psychiatrist", "Clinic", "Other"];
const CONTACT_EMAIL = "hello@simplyrefer.com.au";

export default function Contact() {
  useSeo({
    title: "Contact SimplyRefer | GP Practices and Clinicians",
    description:
      "Contact SimplyRefer about GP practice access, clinician directory access or clinic registration.",
    canonicalPath: "/contact",
  });

  const [form, setForm] = useState({ name: "", email: "", organisation: "", role: "GP practice", message: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.organisation.trim() || !form.role.trim() || !form.message.trim()) {
      setError("Please complete all fields before sending your enquiry.");
      return;
    }
    setError("");
    const subject = encodeURIComponent(`SimplyRefer enquiry — ${form.role}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nOrganisation: ${form.organisation}\nRole: ${form.role}\n\n${form.message}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <MarketingLayout active="/contact">
      <Hero
        eyebrow="Contact"
        h1="Get in touch with SimplyRefer."
        sub="Whether you are a GP practice, psychologist, psychiatrist or clinic, we would be happy to hear from you."
      />

      <section className="bg-slate-50">
        <div className="mx-auto grid max-w-4xl gap-10 px-5 py-14 sm:px-8 md:grid-cols-2">
          <div className="space-y-4 text-slate-600">
            <p className="text-sm">Please contact SimplyRefer if you are interested in:</p>
            <ul className="space-y-2 text-sm">
              <li>• GP practice early access</li>
              <li>• Joining the clinician directory</li>
              <li>• Registering a clinic</li>
              <li>• Learning more about the rollout</li>
            </ul>
            <p className="pt-2 text-sm">
              Or email us directly at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-primary hover:underline">{CONTACT_EMAIL}</a>.
            </p>
            <p className="rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-500" data-testid="contact-mailto-note">
              This form opens your email app with a pre-filled message. Please press send to complete your enquiry.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="contact-form">
            <div>
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" value={form.name} onChange={set("name")} required data-testid="contact-name" />
            </div>
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={form.email} onChange={set("email")} required data-testid="contact-email" />
            </div>
            <div>
              <Label htmlFor="c-org">Organisation</Label>
              <Input id="c-org" value={form.organisation} onChange={set("organisation")} required data-testid="contact-organisation" />
            </div>
            <div>
              <Label htmlFor="c-role">Role</Label>
              <select
                id="c-role"
                value={form.role}
                onChange={set("role")}
                className="mt-1 flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="contact-role"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="c-message">Message</Label>
              <Textarea id="c-message" rows={4} value={form.message} onChange={set("message")} required data-testid="contact-message" />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="contact-error">{error}</p>
            )}
            <Button type="submit" variant="brand" className="w-full" data-testid="contact-submit">Send message</Button>
          </form>
        </div>
      </section>
    </MarketingLayout>
  );
}
